import
{
    useL10n
} from '@ews/react-localization-context';

import
{
    IonBadge,
    IonButton,
    IonCard,
    IonNote
} from '@ionic/react';

import React, {
    FC,
    ReactNode,
    useEffect
} from 'react';

import
{
    EventCategoryIcon
} from '../Icons';

import
{
    useOperateSystem,
    useSystem,
    useSystemPermissions
} from '../ReactData/system';

import
{
    useIsMobile
} from '../Responsive';

import buttonStyle from './ButtonContainer.module.scss';

import
{
    ButtonContainerProps
} from './types';

import
{
    useAuthorization
} from '../Authorization';

import
{
    AuthorizationLevel,
    NoDeviceNumber,
    NumberGroup,
    RTMCategory
} from '@ews/zlt-events';

import
{
    getActionButton
} from '@ews/zlt-operate';

import Icon, {
    IconName
} from '@ews/react-icons';


import
{
    LicenseType,
    SystemOperate,
    SystemRTM,
    SystemStatus,
    SystemType,
    User
} from '@ews/react-data';

import
{
    Timer
} from '../Icons/Timer';

import
{
    useSystemStatus
} from '../System/SystemStatus';

import
{
    distributeRTMFields
} from '../Event/helper.ts/distributeRTMFields';

import { Link } from 'react-router-dom';
import { rtmSortOrder } from '../Event/EventOverview/EventOverview';
import
{
    EventTranslation,
    RTMFieldOrder
} from '../Event/types';
import { useHasLicense } from '../HasLicense';
import { useMyEventDisplay, useMyProfile } from '../Login/LoginProvider';
import { useNamedRoutes } from '../NamedRoutes';

type ButtonProperties = {
    systemId: string;
    rtm: SystemRTM[],
};

const useButton = (systemId: string) =>
{
    const { authorizationLevel } = useAuthorization();
    const systemStatus = useSystemStatus(systemId);
    const hasLicense = useHasLicense(LicenseType.OPERATE_AND_NOTIFICATION);

    const canOperate = Boolean(systemId && systemStatus === SystemStatus.ONLINE);
    const mayOperate = Boolean(canOperate && authorizationLevel > AuthorizationLevel.LEVEL1);

    return { canOperate, mayOperate, hasLicense };
};

const ButtonResetPanel: FC<ButtonProperties> = ({
    systemId,
}) =>
{
    const { translate: t } = useL10n();
    const permission = useSystemPermissions(systemId);
    const operate = useOperateSystem();
    const { mayOperate, hasLicense } = useButton(systemId);

    if (permission.exposePanelReset) {
        return <IonButton disabled={!(hasLicense && mayOperate)} className={buttonStyle["no-border"]} color={'danger'}
            onClick={async (e) =>
            {
                await operate(systemId, { command: 'DEACTIVATE' } as SystemOperate);
            }} ><p className={buttonStyle.reset}>{"RESET"}</p>
        </IonButton>;
    }
    return <></>;
};

const ButtonBuzzer: FC<ButtonProperties> = ({
    systemId,
    rtm,
}) =>
{
    const permission = useSystemPermissions(systemId);
    const operate = useOperateSystem();

    const { canOperate, hasLicense } = useButton(systemId);
    const [icon, command, state] = getActionButton('Buzzer', rtm.map(rtm => rtm.eventKindType!));

    if (permission.exposeInternalBuzzer) {
        return <IonButton disabled={!(hasLicense && (canOperate || command))} className={buttonStyle[state]} color={'light'} onClick={async (e) =>
        {
            if (command) await operate(systemId!, { command } as SystemOperate);
        }} >
            <Icon icon={icon} />
        </IonButton >;
    }

    return <></>;
};

const IconTransDev: FC<{
    icon: IconName;
    state: string;
    reactionTimer?: number,
    explorationTimer?: number;
}> = ({
    icon,
    state,
    reactionTimer = 30,
    explorationTimer = 600
}) =>
    {
        if (icon === 'none') {

            const time = state === 'reactionTimer' ? reactionTimer : explorationTimer;
            const highlight = state === 'reactionTimer' ? "#efc100" : "#e6001c";

            return (<Timer
                className={buttonStyle.timer}
                highlight={highlight}
                strokeWidth={10}
                fontWeight={'bold'}
                fontSize={'15pt'}
                time={time}
                reset={state}
            />);

        } else {
            return <Icon icon={icon} />;
        }
    };

const ButtonTransDev: FC<ButtonProperties> = ({
    systemId,
    rtm,
}) =>
{
    const permission = useSystemPermissions(systemId);
    const operate = useOperateSystem();
    const { transmittingDeviceElement } = permission;
    const transDevRTM = rtm
        .filter(rtm => (rtm.numberGroup === NumberGroup.TransmissionDevice && rtm.zone === transmittingDeviceElement))
        .map(rtm => rtm.eventKindType!);

    const { mayOperate, hasLicense } = useButton(systemId);
    const [icon, command, state] = getActionButton('TransDev', transDevRTM);

    const operation = {
        numberGroup: NumberGroup.TransmissionDevice,
        firstZone: transmittingDeviceElement,
        lastZone: transmittingDeviceElement,
    };

    if (permission.exposeTransmittingDevice) {
        return <IonButton disabled={!(hasLicense && (mayOperate || state === "reactionTimer"))} className={buttonStyle[state]} color={'light'} onClick={async (e) =>
        {
            if (command) await operate(systemId!, { command, ...operation } as SystemOperate);
        }} >
            <IconTransDev
                icon={icon}
                state={state}
                reactionTimer={permission.transmittingDeviceReactionTimer}
                explorationTimer={permission.transmittingDeviceExplorationTimer}
            />
        </IonButton >;
    }

    return <></>;
};

const ButtonAlarmDev: FC<ButtonProperties> = ({
    systemId,
    rtm
}) =>
{
    const system = useSystem(systemId);
    const permission = useSystemPermissions(systemId);
    const operate = useOperateSystem();

    //const rtm = useSystemRTM(systemId).map(rtm => rtm.eventKindType!);
    const { mayOperate, hasLicense } = useButton(systemId);
    const { alarmingDeviceFirstElement = 0, alarmingDeviceLastElement = NoDeviceNumber } = permission;

    const alarmDevRTM = rtm
        .filter(rtm =>
        {
            if (rtm.numberGroup !== NumberGroup.AlarmingDevice) return false;

            if (system.systemType === SystemType.BC216) {
                return alarmingDeviceFirstElement === 0x3E8 || rtm.zone === alarmingDeviceFirstElement;
            }

            return rtm.zone >= alarmingDeviceFirstElement && rtm.zone <= alarmingDeviceLastElement;
        })
        .map(rtm => rtm.eventKindType!);

    const [icon, command, state] = getActionButton('AlarmDev', alarmDevRTM);

    const operation = {
        numberGroup: NumberGroup.AlarmingDevice,
        firstZone: alarmingDeviceFirstElement,
        lastZone: alarmingDeviceLastElement,
    };

    if (permission.exposeAlarmingDevice) {
        return <IonButton disabled={!(hasLicense && (mayOperate || permission.operateAeAuthLevel === 'level1'))} className={buttonStyle[state]} color={'light'} onClick={async (e) =>
        {
            if (command) await operate(systemId!, { command, ...operation } as SystemOperate);
        }} >
            <Icon icon={icon} />
        </IonButton >;
    }

    return <></>;
};

const ButtonsBackground = ({ color, children, className, mobileView = false }:
    { color?: string, children: ReactNode, className?: string, mobileView?: boolean; }) => mobileView ? <div className={`${className} ${buttonStyle.mobile}`}>{children}</div> :
        <IonCard color={color} className={`${className} ${buttonStyle.status}`}>{children}</IonCard>;

const ActionButtons = ({ children, className, mobileView = false }:
    {
        children: ReactNode,
        className: string,
        mobileView?: boolean,
    }) => mobileView ? <>{children}</> : <div className={className}>{children}</div>;


const EventLink: FC<{ permission: boolean, route: string, children: ReactNode; }> = ({ permission, route, children }) => permission ? <Link to={route}>{children}</Link> : <>{children}</>;


const ButtonContainer: React.FC<ButtonContainerProps> = ({
    systemId,
    rtm
}) =>
{
    const mobileView = useIsMobile();
    const { translate: t } = useL10n();

    const system = useSystem(systemId || "");
    const { generate } = useNamedRoutes();
    const eventDisplay = useMyEventDisplay();
    const me = useMyProfile();

    useEffect(() =>
    {

    }, [systemId]);

    const rtmFields = distributeRTMFields(rtm);

    const hasDetailView = useHasLicense(LicenseType.DETAIL_VIEW);


    return (
        <ButtonsBackground color="tertiary" mobileView={mobileView}>

            {!mobileView && <div className={buttonStyle.events}>

                {RTMFieldOrder.sort(rtmSortOrder).map((categoryType) =>
                {
                    const category: RTMCategory = RTMCategory[categoryType];
                    const rtm: SystemRTM[] = rtmFields[category]!;

                    if (!system.hasFloodingZone) {
                        rtmFields[RTMCategory.ExtinguishingEvent] = null;
                    }

                    if (system.systemType === SystemType.BC216) {
                        rtmFields[RTMCategory.Confirmation] = null;
                    }

                    if (!rtm) return <React.Fragment key={categoryType} />;

                    const route = systemId && `tabCategory${categoryType}` in eventDisplay ?
                        (eventDisplay as Record<string, any>)[`tabCategory${categoryType}`] === 0 ? generate("system:events", { "id": systemId }) : generate("system:events-page2", { "id": systemId })
                        : "";

                    const greyScale = !Boolean(rtm.length);

                    return <div className={buttonStyle.container} key={categoryType}>

                        <EventLink route={route} permission={hasDetailView}>
                            <div className={buttonStyle.eventIcon}>
                                <EventCategoryIcon greyScale={greyScale} category={categoryType} />
                            </div>
                            <div className={buttonStyle.eventName}>
                                <IonNote >{t(EventTranslation[categoryType])}</IonNote>
                            </div>
                            <div>
                                <IonBadge color={'danger'} >{rtm.length}</IonBadge>
                            </div>
                        </EventLink>
                    </div>;
                })
                }
            </div>}

            <ActionButtons className={buttonStyle.desktop} mobileView={mobileView}>
                {<>

                    <ButtonAlarmDev
                        rtm={rtm}
                        systemId={systemId!}
                    />
                    <ButtonTransDev
                        rtm={rtm}
                        systemId={systemId!}
                    />
                    <ButtonBuzzer
                        rtm={rtm}
                        systemId={systemId!}
                    />
                    <ButtonResetPanel
                        rtm={rtm}
                        systemId={systemId!}
                    />
                </>
                }
            </ActionButtons>
        </ButtonsBackground >
    );
};

export default ButtonContainer;
